import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import LockIcon from '@components/icons/LockIcon'
import { POneEvent } from '@modules/auth/hooks/types'
import { Box, Typography } from '@mui/material'

import { UpdatedLabel } from '../index.styles'
import { ProfileEventsContainer, ProfileEventsHeader } from './index.styles'

interface ProfileEventsProps {
  events: (POneEvent & { profileCreatedAt: string })[]
  name: string
  lastViewedAt?: string
}

const ProfileEvents = (props: ProfileEventsProps) => {
  const { events, name, lastViewedAt } = props

  const { t } = useTranslation()
  const currentDate = useMemo(() => DateTime.now(), [])
  const currentEvents = useMemo(
    () =>
      events
        .filter(event =>
          event.endDate ? currentDate.startOf('day') <= DateTime.fromISO(event.endDate).startOf('day') : !event.hidden,
        )
        .sort((eventA, eventB) => {
          const dateA = new Date(eventA.endDate || eventA.startDate).getTime()
          const dateB = new Date(eventB.endDate || eventB.startDate).getTime()
          return dateA - dateB
        }),
    [events, currentDate],
  )

  const pastEvents = useMemo(() => {
    const currentEventsIds = currentEvents.map(event => event.id)
    return events
      .filter(event => !currentEventsIds.includes(event.id))
      .sort((eventA, eventB) => {
        const dateA = new Date(eventA.endDate || eventA.startDate).getTime()
        const dateB = new Date(eventB.endDate || eventB.startDate).getTime()
        return dateB - dateA
      })
  }, [currentEvents, events])

  const isUpdated = useMemo(() => {
    if (!lastViewedAt) {
      return false
    }

    const lastViewedAtDate = DateTime.fromISO(lastViewedAt)
    return events.some(
      event =>
        lastViewedAtDate < DateTime.fromISO(event.startDate) && lastViewedAtDate < DateTime.fromISO(event.profileCreatedAt),
    )
  }, [lastViewedAt, events])

  return (
    <Box marginBottom="12px" position="relative">
      {isUpdated && <UpdatedLabel isAbsolute>{t(translationKeys.UPDATED)}</UpdatedLabel>}
      <ProfileEventsHeader>{t(translationKeys.PROFILE_EVENTS)}</ProfileEventsHeader>
      <ProfileEventsContainer isUpdated={isUpdated}>
        {!!currentEvents?.length && (
          <div>
            <Typography variant="body2" fontWeight={700}>
              {t(translationKeys.PROFILE_CURRENT_EVENTS, { name })}
            </Typography>

            {currentEvents.map(event => (
              <Box key={event.id} display="flex" justifyContent="space-between">
                <Typography variant="body2">{event.name}</Typography>
                {event.hidden && <LockIcon color="primary" fontSize="small" />}
              </Box>
            ))}
          </div>
        )}
        {!!pastEvents?.length && (
          <div>
            <Typography variant="body2" fontWeight={700}>
              {t(translationKeys.PROFILE_PAST_EVENTS)}
            </Typography>

            {pastEvents.map(event => (
              <Box key={event.id} display="flex" justifyContent="space-between">
                <Typography variant="body2">{event.name}</Typography>
                {event.hidden && <LockIcon color="primary" fontSize="small" />}
              </Box>
            ))}
          </div>
        )}
      </ProfileEventsContainer>
    </Box>
  )
}

export default ProfileEvents
